import { useMemo, useRef, useState } from 'react';
import {
  type ColumnDef,
  type ColumnFiltersState,
  type Row,
  type RowData,
  type RowSelectionState,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ChevronLeftIcon, ChevronRightIcon, SearchIcon, XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '../../../lib';
import { Checkbox } from '../../forms/checkbox';
import { Input } from '../../forms/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../forms/select';
import { Button } from '../button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../table';
import { DataTableDateFilter, type DateRange } from './data-table-date-filter';
import { DataTableFacetedFilter, type FacetOption } from './data-table-faceted-filter';
import { DataTableNumberFilter, type NumberRange } from './data-table-number-filter';

declare module '@tanstack/react-table' {
  // biome-ignore lint/correctness/noUnusedVariables: augmentation must match the base signature
  interface ColumnMeta<TData extends RowData, TValue> {
    /** Pin the column to the horizontal scroll edge. */
    sticky?: 'left' | 'right';
    /** Extra classes for the column's header and body cells. */
    className?: string;
    /** Inline style for the column's header and body cells. */
    style?: React.CSSProperties;
  }
}

const stickyClassName = (sticky: 'left' | 'right' | undefined, variant: 'head' | 'cell') =>
  sticky &&
  cn(
    'sticky z-20',
    sticky === 'left' ? 'left-0' : 'right-0',
    // Opaque background so scrolled columns don't bleed through the pinned cell.
    variant === 'head' ? 'bg-muted' : 'bg-card',
  );

/** Sentinel filter value matching rows with no value (null/undefined/empty string/empty array). */
export const EMPTY_FILTER_VALUE = 'NO_VALUE';

export const isEmptyValue = (value: unknown) =>
  value == null || value === '' || (Array.isArray(value) && value.length === 0);

/** Faceted (multi-value) column filter: keeps rows whose scalar value is one of the selected. */
export function facetedFilterFn<TData>(row: Row<TData>, columnId: string, filterValue: string[]) {
  if (!filterValue?.length) {
    return true;
  }
  const raw = row.getValue(columnId);
  if (isEmptyValue(raw)) {
    return filterValue.includes(EMPTY_FILTER_VALUE);
  }
  const value = String(raw).toLowerCase();
  return filterValue.some(selected => selected.toLowerCase() === value);
}

export function numberRangeFilterFn<TData>(
  row: Row<TData>,
  columnId: string,
  filterValue: NumberRange,
) {
  const raw = row.getValue(columnId);
  if (raw == null || raw === '') {
    return false;
  }
  const value = Number(raw);
  if (Number.isNaN(value)) {
    return false;
  }
  const { min, max } = filterValue ?? {};
  return (min == null || value >= min) && (max == null || value <= max);
}

export function dateRangeFilterFn<TData>(
  row: Row<TData>,
  columnId: string,
  filterValue: DateRange,
) {
  const raw = row.getValue(columnId);
  if (raw == null || raw === '') {
    return false;
  }
  // ISO yyyy-mm-dd compares correctly as a string.
  const value = String(raw).slice(0, 10);
  const { from, to } = filterValue ?? {};
  return (!from || value >= from) && (!to || value <= to);
}

/** Global search: every whitespace-separated word must appear in some column value (AND, case-insensitive). */
function multiWordGlobalFilter<TData>(row: Row<TData>, _columnId: string, filterValue: string) {
  const words = filterValue.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) {
    return true;
  }
  const haystack = row
    .getAllCells()
    .map(cell => cell.getValue())
    .filter(value => value != null)
    .join(' ')
    .toLowerCase();
  return words.every(word => haystack.includes(word));
}

export interface DataTableFilter {
  columnId: string;
  title: string;
  type?: 'faceted' | 'number' | 'date';
  options?: FacetOption[];
}

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  searchable?: boolean;
  searchPlaceholder?: string;
  filters?: DataTableFilter[];
  enableRowSelection?: boolean;
  getRowId?: (row: TData) => string;
  renderSelectionActions?: (
    selected: TData[],
    actions: { clearSelection: () => void; selectAll: () => void; filteredCount: number },
  ) => React.ReactNode;
  pageSize?: number;
  pageSizeOptions?: number[];
  emptyMessage?: React.ReactNode;
  dense?: boolean;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  searchable = false,
  searchPlaceholder,
  filters,
  enableRowSelection = false,
  getRowId,
  renderSelectionActions,
  pageSize = 10,
  pageSizeOptions = [10, 20, 50, 100],
  emptyMessage,
  dense = false,
}: DataTableProps<TData, TValue>) {
  const { t } = useTranslation();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const lastSelectedRowId = useRef<string | null>(null);
  const shiftHeld = useRef(false);

  const tableColumns = useMemo(() => {
    if (!enableRowSelection) {
      return columns;
    }
    const selectionColumn: ColumnDef<TData, TValue> = {
      id: '__select',
      enableSorting: false,
      meta: { sticky: 'left', style: { paddingLeft: 8, paddingRight: 8 } },
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && 'indeterminate')
          }
          onCheckedChange={value => table.toggleAllPageRowsSelected(!!value)}
          aria-label={t('dataTable.selectAll')}
        />
      ),
      cell: ({ row, table }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onClick={event => {
            shiftHeld.current = event.shiftKey;
          }}
          onCheckedChange={value => {
            const rows = table.getRowModel().rows;
            const start = rows.findIndex(({ id }) => id === lastSelectedRowId.current);
            const end = rows.findIndex(({ id }) => id === row.id);
            if (shiftHeld.current && start !== -1 && end !== -1 && start !== end) {
              const [from, to] = start < end ? [start, end] : [end, start];
              setRowSelection(previous => {
                const next = { ...previous };
                for (let index = from; index <= to; index++) {
                  next[rows[index].id] = !!value;
                }
                return next;
              });
            } else {
              row.toggleSelected(!!value);
            }
            lastSelectedRowId.current = row.id;
          }}
          aria-label={t('dataTable.selectRow')}
        />
      ),
    };
    return [selectionColumn, ...columns];
  }, [columns, enableRowSelection, t]);

  const table = useReactTable({
    data,
    columns: tableColumns,
    state: { sorting, columnFilters, globalFilter, rowSelection },
    initialState: { pagination: { pageSize } },
    enableRowSelection,
    getRowId,
    globalFilterFn: multiWordGlobalFilter,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
  });

  const rowCount = table.getFilteredRowModel().rows.length;
  const isFiltered = columnFilters.length > 0 || globalFilter.length > 0;
  const selectedRows = table.getSelectedRowModel().rows;
  const hasToolbar = searchable || (filters?.length ?? 0) > 0;

  return (
    <div className="space-y-3">
      {hasToolbar && (
        <div className="flex flex-wrap items-center gap-2">
          {searchable && (
            <Input
              className="max-w-xs flex-1"
              icon={<SearchIcon className="size-4" />}
              value={globalFilter}
              onChange={event => setGlobalFilter(event.target.value)}
              placeholder={searchPlaceholder ?? t('dataTable.search')}
              clearable
              onClear={() => setGlobalFilter('')}
            />
          )}
          {filters?.map(filter => {
            const column = table.getColumn(filter.columnId);
            if (filter.type === 'number') {
              return (
                <DataTableNumberFilter key={filter.columnId} column={column} title={filter.title} />
              );
            }
            if (filter.type === 'date') {
              return (
                <DataTableDateFilter key={filter.columnId} column={column} title={filter.title} />
              );
            }
            return (
              <DataTableFacetedFilter
                key={filter.columnId}
                column={column}
                title={filter.title}
                options={filter.options}
              />
            );
          })}
          {isFiltered && (
            <Button
              variant="ghost"
              size="sm"
              className="h-9 px-2"
              onClick={() => {
                table.resetColumnFilters();
                setGlobalFilter('');
              }}
            >
              {t('dataTable.reset')}
              <XIcon className="ml-1 size-4" />
            </Button>
          )}
        </div>
      )}

      {enableRowSelection && renderSelectionActions && selectedRows.length > 0 && (
        <div className="flex items-center justify-between rounded-md border bg-muted/40 px-3 py-2 text-sm">
          <span className="text-muted-foreground">
            {selectedRows.length}{' '}
            {selectedRows.length > 1 ? t('dataTable.selectedPlural') : t('dataTable.selected')}
          </span>
          {renderSelectionActions(
            selectedRows.map(row => row.original),
            {
              clearSelection: () => table.resetRowSelection(),
              selectAll: () => table.toggleAllRowsSelected(true),
              filteredCount: rowCount,
            },
          )}
        </div>
      )}

      <div
        className={cn(
          'overflow-hidden rounded-lg border bg-card',
          dense && '[&_td]:py-1.5 [&_th]:h-10',
        )}
      >
        {/* border-separate: sticky columns only reserve width when the table is not border-collapse */}
        <Table className="border-separate border-spacing-0">
          <TableHeader className="bg-muted/40">
            {table.getHeaderGroups().map(headerGroup => (
              <TableRow key={headerGroup.id} className="hover:bg-transparent [&>th]:border-b">
                {headerGroup.headers.map(header => (
                  <TableHead
                    key={header.id}
                    className={cn(
                      stickyClassName(header.column.columnDef.meta?.sticky, 'head'),
                      header.column.columnDef.meta?.className,
                    )}
                    style={header.column.columnDef.meta?.style}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map(row => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() ? 'selected' : undefined}
                  className="group [&>td]:border-b last:[&>td]:border-b-0"
                >
                  {row.getVisibleCells().map(cell => (
                    <TableCell
                      key={cell.id}
                      className={cn(
                        stickyClassName(cell.column.columnDef.meta?.sticky, 'cell'),
                        cell.column.columnDef.meta?.className,
                      )}
                      style={cell.column.columnDef.meta?.style}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow className="hover:bg-transparent">
                <TableCell
                  colSpan={table.getAllColumns().length}
                  className="h-24 text-center text-muted-foreground"
                >
                  {emptyMessage ?? t('dataTable.empty')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between gap-4 text-sm text-muted-foreground">
        <div className="flex items-center gap-2">
          <span>{t('dataTable.rowsPerPage')}</span>
          <Select
            value={String(table.getState().pagination.pageSize)}
            onValueChange={value => table.setPageSize(Number(value))}
          >
            <SelectTrigger className="h-8 w-[68px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {pageSizeOptions.map(option => (
                <SelectItem key={option} value={String(option)}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-4">
          <span className="flex items-center gap-2 tabular-nums">
            <span>
              {t('dataTable.page', {
                current: table.getState().pagination.pageIndex + 1,
                total: table.getPageCount() || 1,
              })}
            </span>
            <span className="text-border">·</span>
            <span>
              {rowCount} {rowCount > 1 ? t('dataTable.rows') : t('dataTable.row')}
            </span>
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="size-8"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              aria-label={t('dataTable.previousPage')}
            >
              <ChevronLeftIcon className="size-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="size-8"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              aria-label={t('dataTable.nextPage')}
            >
              <ChevronRightIcon className="size-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
